import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Tooltip } from 'antd';
import {
  BarChartOutlined,
  DatabaseOutlined,
  FileWordOutlined,
  SearchOutlined,
  SyncOutlined,
} from '@ant-design/icons';

import { Bento, PageHeader, TabsTruot, Tile, TileBody } from '../../../components/shared';
import { Badge, Button, Empty, Skeleton } from '../../../components/ui';
import { useDieuHuong, useDuongDan } from '../../../hooks/useDuongDan';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import { useToast } from '../../../lib/toast';
import { DaiLoi } from '../components/chung/VungDuLieu';
import { NhanLoaiNguon } from '../components/chung/LuoiNguon';
import { DaKhaiTab } from '../components/kho/DaKhaiTab';
import { KetQuaDoThongKe } from '../components/kho/KetQuaDoThongKe';
import { KhaiBangDialog } from '../components/kho/KhaiBangDialog';
import { KhaiBaoTab } from '../components/kho/KhaiBaoTab';
import { NguonSyncTab } from '../components/kho/NguonSyncTab';
import { ThuatNguTab } from '../components/kho/ThuatNguTab';
import { QUYEN } from '../constants';
import { useDanhSachNguon } from '../hooks/useChung';
import {
  CHO_BAT_DAU_MS,
  useDocLuocDo,
  useDongBo,
  useDoThongKe,
  useDoTim,
  useGhiDeXuat,
  useTrangThaiNguon,
} from '../hooks/useKho';
import { useMoTaLoi } from '../hooks/useMoTaLoi';
import { HUONG_DAN, TEN_TEP } from '../nghiep-vu/huong-dan-khai-bao';
import { khoaDeXuat } from '../nghiep-vu/de-xuat';
import { taiVe, taoDocx } from '../nghiep-vu/tao-docx';
import type { BangBoQua, Finding, IntrospectBang, RunProfileResult, SyncOutcome } from '../types';

type TabKey = 'nguon' | 'khai' | 'dakhai' | 'thuatngu';

/** Nút phụ trợ trên thanh: biểu tượng ăn màu, CHỮ thì không — tô cả nút thì nó nặng ngang nút "Đồng bộ ngay". */
function NutHanhDong({
  icon,
  nhan,
  goiY,
  dangChay,
  tat,
  onClick,
}: {
  icon: ReactNode;
  nhan: string;
  goiY: string;
  dangChay?: boolean;
  tat?: boolean;
  onClick: () => void;
}) {
  return (
    // `span` bọc: nút `disabled` không phát sự kiện chuột, mà đúng lúc bị khoá mới cần lời giải thích.
    <Tooltip title={goiY}>
      <span className="inline-flex">
        <Button variant="secondary" size="sm" icon={icon} loading={dangChay} disabled={tat} onClick={onClick}>
          {nhan}
        </Button>
      </span>
    </Tooltip>
  );
}

/**
 * Kho dữ liệu của MỘT nguồn (`/tro-ly-ai/ban-sao/:nguon`) — nối, đồng bộ, khai báo nghiệp vụ, dạy từ vựng (port
 * `ReplicaView.tsx` native).
 *
 * Bốn tab theo GIAI ĐOẠN công việc, và thứ tự tab là thứ tự nên làm: nối nguồn → khai báo → soát lời khai → thuật ngữ.
 * Câu hỏi nghiệp vụ SINH RA TỪ bản khai, nên duyệt nó trước khi khai là duyệt xong phải sinh lại.
 */
export function ReplicaView() {
  const { nguon: thamSoNguon = '' } = useParams();
  const sourceId = decodeURIComponent(thamSoNguon);
  // `key` theo nguồn: đổi nguồn là dựng lại màn, việc đang dở của nguồn này không sang nguồn kia.
  return <ManNguon key={sourceId} sourceId={sourceId} />;
}

function ManNguon({ sourceId }: { sourceId: string }) {
  const { t, locale } = useT();
  const toast = useToast();
  const moTaLoi = useMoTaLoi();
  const dieuHuong = useDieuHuong();
  const duongDan = useDuongDan();
  const q = useModulePermissions(QUYEN);
  const [thamSo] = useSearchParams();
  const bangTs = thamSo.get('bang');
  const cotTs = thamSo.get('cot');

  // `null` = người dùng CHƯA tự chọn → tab do trạng thái nguồn quyết. Đã bấm thì lựa chọn của họ thắng và không nhảy nữa.
  const [tabChon, setTabChon] = useState<TabKey | null>(() => (bangTs ? 'khai' : null));
  /** Tăng khi màn hình TỰ đổi tab (sau Đồng bộ, Dò…), để thanh tab dựng lại đúng tab mới; người bấm thì không dựng lại. */
  const [phienTab, setPhienTab] = useState(0);
  const chuyenTab = (k: TabKey) => {
    setTabChon(k);
    setPhienTab((n) => n + 1);
  };
  // Kết quả chu kỳ theo từng bảng: máy chủ chạy nền và chỉ trả `started`, nên như native khối này không còn nguồn dữ liệu.
  const outcomes: SyncOutcome[] | null = null;
  const [findings, setFindings] = useState<Finding[] | null>(null);
  const [theoDoiDen, setTheoDoiDen] = useState(0);
  // `null` = chưa đọc lược đồ lần nào; khác null thì mở hộp chọn bảng.
  const [luocDo, setLuocDo] = useState<IntrospectBang[] | null>(null);
  const [canhBaoLuocDo, setCanhBaoLuocDo] = useState<string[]>([]);
  const [boQuaLuocDo, setBoQuaLuocDo] = useState<BangBoQua[]>([]);
  const [ketQuaDo, setKetQuaDo] = useState<RunProfileResult | null>(null);
  const [daGhi, setDaGhi] = useState<Set<string>>(new Set());

  const sources = useDanhSachNguon();
  // Nguồn API không có kho nào để khai hay đồng bộ — nó sống ở Truy vấn mẫu.
  const nguon = useMemo(() => (sources.data ?? []).find((s) => s.id === sourceId && s.kind !== 'api'), [sources.data, sourceId]);
  const idHienTai = nguon?.id ?? '';
  const laDsn = nguon?.kind === 'dsn';
  const trucTiep = laDsn && nguon?.accessMode === 'live';

  const status = useTrangThaiNguon(idHienTai, theoDoiDen);
  const dongBo = useDongBo(idHienTai);
  const docLuocDo = useDocLuocDo(idHienTai);
  const doThongKe = useDoThongKe(idHienTai);
  const doTim = useDoTim(idHienTai);
  const ghiDeXuat = useGhiDeXuat(idHienTai);

  // Tab mở sẵn suy từ trạng thái: chưa khai bảng nào → "Kết nối & đồng bộ"; đã có dữ liệu → "Khai báo". Mở cứng tab đầu
  // thì người đã đồng bộ xong tuần trước vẫn phải tự tìm sang tab hai MỖI LẦN vào.
  const soBang = status.data?.tables.length ?? 0;
  const daCoDuLieu = (status.data?.tables ?? []).some((x) => x.rowCount > 0);
  const tabGoiY: TabKey = soBang === 0 ? 'nguon' : daCoDuLieu || trucTiep ? 'khai' : 'nguon';
  // Nguồn trả lời theo khuôn: hai tab khai bằng câu hỏi không có hiệu lực, nên không hiện.
  const theoKhuon = nguon?.cheDo === 'khuon';
  const tabHopLe = (k: TabKey) => !theoKhuon || k === 'nguon' || k === 'thuatngu';
  const tabMuon: TabKey = tabChon ?? tabGoiY;
  const tab: TabKey = tabHopLe(tabMuon) ? tabMuon : 'nguon';

  // Báo khi chu kỳ vừa XONG trong lúc màn hình đang mở — chuyển từ chạy sang không chạy, không phải mỗi lần tải trang.
  const dangChayTruoc = useRef<boolean | undefined>(undefined);
  const tienDo = status.data?.dongBo;
  useEffect(() => {
    const dang = tienDo?.dangChay;
    if (dangChayTruoc.current && dang === false) {
      const dung = (status.data?.tables ?? []).filter((b) => b.lastStatus === 'stopped');
      if (tienDo?.biNgat) toast.error(t('troLyAiKho.dongBoBiNgat', { bang: tienDo.bang ?? '' }));
      else if (dung.length) {
        // Chỉ TÊN bảng: lý do nằm đúng chỗ người ta xử lý nó — trong bảng trạng thái.
        toast.warning(
          t('troLyAiKho.dongBoXongCoDung', { n: soVN(dung.length, locale), ds: dung.map((b) => b.table).join(', ') }),
        );
      } else toast.success(t('troLyAiKho.dongBoXong', { n: soVN(tienDo?.soBang ?? 0, locale) }));
    }
    dangChayTruoc.current = dang;
    // Chỉ chạy theo nhịp chuyển của `dangChay` — xem chú thích trên.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tienDo?.dangChay]);

  /** Máy chủ chỉ BẮT ĐẦU chu kỳ rồi trả về (lượt chép đầy đủ có thể dài hơn trần của gateway) — theo dõi qua trạng thái. */
  const chayDongBo = () =>
    dongBo.mutate(undefined, {
      onSuccess: (kq) => {
        toast.success(t('troLyAiKho.dongBoBatDau', { n: soVN(kq.tables, locale) }));
        chuyenTab('nguon');
        setTheoDoiDen(Date.now() + CHO_BAT_DAU_MS);
      },
      onError: (e) => toast.error(t('troLyAiKho.khongDongBoDuoc', { loi: moTaLoi(e) })),
    });

  /** "Chọn bảng từ CSDL" ĐỌC LƯỢC ĐỒ THẬT rồi mở hộp chọn bảng. */
  const chayDocLuocDo = () =>
    docLuocDo.mutate(undefined, {
      onSuccess: (kq) => {
        if (!kq.tables.length) {
          // Rỗng KHÔNG có nghĩa là CSDL rỗng: thiếu `USAGE` trên lược đồ cũng ra rỗng — nói ra ngay tại đây.
          toast.warning(t('troLyAiKho.luocDoRong'));
          return;
        }
        setCanhBaoLuocDo(kq.canhBao ?? []);
        setBoQuaLuocDo(kq.boQua ?? []);
        setLuocDo(kq.tables);
      },
      onError: (e) => toast.error(t('troLyAiKho.khongDocDuocLuocDo', { loi: moTaLoi(e) })),
    });

  const chayDoThongKe = () => {
    toast.info(t('troLyAiKho.dangDoThongKe'));
    doThongKe.mutate(undefined, {
      onSuccess: setKetQuaDo,
      onError: (e) => toast.error(t('troLyAiKho.khongDoDuocThongKe', { loi: moTaLoi(e) })),
    });
  };

  const chayDoTim = () =>
    doTim.mutate(undefined, {
      onSuccess: ({ findings: kq, hetGio, boQua }) => {
        setFindings(kq);
        // Kéo về tab hiện KẾT QUẢ: danh sách đề xuất chỉ vẽ ở tab "Kết nối & đồng bộ".
        chuyenTab('nguon');
        if (hetGio) toast.warning(t('troLyAiKho.doTimHetGio', { n: soVN(kq.length, locale), boQua: soVN(boQua, locale) }));
        else
          toast.success(
            kq.length ? t('troLyAiKho.timThayDeXuat', { n: soVN(kq.length, locale) }) : t('troLyAiKho.khongThayDeXuat'),
          );
      },
      onError: (e) => toast.error(t('troLyAiKho.khongDoDuoc', { loi: moTaLoi(e) })),
    });

  const ghi = (f: Finding) =>
    ghiDeXuat.mutate(f, {
      onSuccess: () => {
        setDaGhi((p) => new Set(p).add(khoaDeXuat(f)));
        toast.success(t('troLyAiKhoSync.daGhiToast', { muc: `${f.table}${f.column ? `.${f.column}` : ''}` }));
      },
      onError: (e) => toast.error(t('troLyAiKhoSync.khongGhiDuoc', { loi: moTaLoi(e) })),
    });

  const breadcrumb = [
    { label: t('nav.nhom.troLiAi') },
    { label: t('nav.muc.khoDuLieu'), onClick: () => dieuHuong('/tro-ly-ai/ban-sao') },
  ];

  if (sources.isPending) {
    return (
      <div className="space-y-4 p-4" aria-busy="true">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (sources.error) {
    return (
      <div className="space-y-4 p-4">
        <PageHeader breadcrumb={breadcrumb} title={t('troLyAiKho.tieuDe')} />
        <DaiLoi loi={sources.error} onThuLai={() => void sources.refetch()} />
      </div>
    );
  }

  if (!nguon) {
    return (
      <div className="space-y-4 p-4">
        <PageHeader breadcrumb={breadcrumb} title={t('troLyAiKho.tieuDe')} />
        <Empty
          icon={DatabaseOutlined}
          title={t('troLyAiKho.khongCoNguon')}
          description={t('troLyAiKho.khongCoNguonMoTa', { ma: sourceId })}
          action={
            <Link to={duongDan('/tro-ly-ai/ban-sao')}>
              <Button variant="primary">{t('troLyAiKho.tatCaNguon')}</Button>
            </Link>
          }
        />
      </div>
    );
  }

  const tabs: { key: TabKey; label: string }[] = (
    [
      { key: 'nguon', label: t('troLyAiKho.tabNguon') },
      { key: 'khai', label: t('troLyAiKho.tabKhai') },
      { key: 'dakhai', label: t('troLyAiKhoDaKhai.tab') },
      // Đứng SAU "Nghiệp vụ đã khai báo" vì nó là bước tiếp theo: khai xong thuộc tính cột rồi mới tới tri thức nhiều cột.
      { key: 'thuatngu', label: t('troLyAiKhoThuatNgu.tab') },
      // "Tri thức gắn kèm" (`TriThucGanKem`) ẩn như native — màn duy nhất đọc nó (Hỏi đáp AI) đã ẩn khỏi thanh điều hướng.
    ] as const
  ).filter((x) => tabHopLe(x.key));

  const noiDung = (k: TabKey): ReactNode => {
    switch (k) {
      case 'nguon':
        return (
          <NguonSyncTab
            sourceId={idHienTai}
            laDsn={laDsn}
            trucTiep={trucTiep}
            outcomes={outcomes}
            findings={findings}
            daGhi={daGhi}
            onGhi={ghi}
            dangGhi={ghiDeXuat.isPending ? (ghiDeXuat.variables ?? null) : null}
            // Xoá bảng DROP kho bản sao, không lùi được — theo quyền XOÁ của module.
            xoaDuoc={q.nguonXoa}
            dongBoDuoc={q.nguonSua}
            onDongBoBatDau={() => setTheoDoiDen(Date.now() + CHO_BAT_DAU_MS)}
          />
        );
      case 'khai':
        return <KhaiBaoTab sourceId={idHienTai} bangCanSoi={bangTs} cotCanSoi={cotTs} suaDuoc={q.nguonSua} />;
      case 'dakhai':
        return <DaKhaiTab sourceId={idHienTai} xoaDuoc={q.nguonXoa} />;
      default:
        return <ThuatNguTab sourceId={idHienTai} />;
    }
  };

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[...breadcrumb, { label: nguon.name }]}
          title={
            <span className="flex flex-wrap items-center gap-2">
              <span className="min-w-0 break-words">{nguon.name}</span>
              <NhanLoaiNguon s={nguon} />
              {trucTiep ? <Badge variant="neutral">{t('troLyAiKho.trucTiep')}</Badge> : null}
            </span>
          }
          subtitle={nguon.moTa || t('troLyAiKho.phuDe')}
          actions={
            <div className="flex flex-wrap items-center gap-2" role="toolbar" aria-label={t('troLyAiKho.thanhHanhDong')}>
              {/* Hướng dẫn đứng TRƯỚC và không bao giờ khoá: người mở màn lần đầu là người cần đọc nó nhất. Dựng tệp lúc
                  bấm từ nội dung trong repo — không kèm `.docx` nhị phân không ai đọc được diff. */}
              <NutHanhDong
                icon={<FileWordOutlined className="text-sky-600" />}
                nhan={t('troLyAiKho.taiHuongDan')}
                goiY={t('troLyAiKho.goiYTaiHuongDan')}
                onClick={() => taiVe(taoDocx(HUONG_DAN), TEN_TEP)}
              />
              {/* Hai nút dò, hai câu hỏi khác nhau: đọc LƯỢC ĐỒ nguồn (chỉ cần kết nối) và dò mã · quan hệ · bảng chết
                  (cần bản sao đã có dữ liệu). */}
              {q.nguonSua ? (
                <NutHanhDong
                  icon={<DatabaseOutlined className="text-violet-600" />}
                  nhan={t('troLyAiKho.khaiBangTuNguon')}
                  goiY={t('troLyAiKho.goiYKhaiBangTuNguon')}
                  dangChay={docLuocDo.isPending}
                  onClick={chayDocLuocDo}
                />
              ) : null}
              <NutHanhDong
                icon={<SearchOutlined className="text-teal-600" />}
                nhan={t('troLyAiKho.doMaQuanHe')}
                goiY={t('troLyAiKho.goiYDoMaQuanHe')}
                dangChay={doTim.isPending}
                onClick={chayDoTim}
              />
              {/* Vạch ngăn: ba nút trái chỉ ĐỌC và đề xuất, nút phải GHI dữ liệu về bản sao. */}
              {q.nguonSua ? (
                <>
                  <span aria-hidden className="mx-1 hidden h-5 border-s border-default sm:block" />
                  {trucTiep ? (
                    <Tooltip title={t('troLyAiKho.goiYDoThongKe')}>
                      <Button size="sm" icon={<BarChartOutlined />} loading={doThongKe.isPending} onClick={chayDoThongKe}>
                        {t('troLyAiKho.doThongKe')}
                      </Button>
                    </Tooltip>
                  ) : (
                    <Tooltip title={t('troLyAiKho.goiYDongBoNgay')}>
                      <Button size="sm" icon={<SyncOutlined />} loading={dongBo.isPending} onClick={chayDongBo}>
                        {t('troLyAiKho.dongBoNgay')}
                      </Button>
                    </Tooltip>
                  )}
                </>
              ) : null}
            </div>
          }
        />

        {theoKhuon ? (
          <p className="rounded-lg border border-primary-200 bg-primary-50 px-3.5 py-2.5 text-sm text-primary-800">
            {t('troLyAiKho.dangTheoKhuon')}{' '}
            <Link to={duongDan(`/tro-ly-ai/khuon/${encodeURIComponent(idHienTai)}`)} className="font-medium underline">
              {t('troLyAiKho.moKhuonNghiepVu')}
            </Link>
          </p>
        ) : null}

        <Bento>
          <Tile>
            <TileBody>
              <TabsTruot
                // Dựng lại khi màn TỰ đổi tab, hoặc khi tab gợi ý đổi lúc người dùng chưa chọn (trạng thái vừa tải xong).
                key={`${phienTab}-${tabChon === null ? tabGoiY : 'chon'}`}
                gon
                aria-label={t('troLyAiKho.tabViec')}
                defaultActiveKey={tab}
                onChange={(k) => setTabChon(k as TabKey)}
                items={tabs.map((x) => ({ key: x.key, label: x.label, children: noiDung(x.key) }))}
              />
            </TileBody>
          </Tile>
        </Bento>

        <KetQuaDoThongKe kq={ketQuaDo} onClose={() => setKetQuaDo(null)} />
        <KhaiBangDialog
          sourceId={idHienTai}
          bang={luocDo}
          canhBao={canhBaoLuocDo}
          boQua={boQuaLuocDo}
          onClose={() => setLuocDo(null)}
        />
      </div>
    </div>
  );
}
